/**
 * ViewToggle — shared Cards/List toggle for every Communications v2
 * surface. Persists selection to localStorage under a caller-supplied
 * key so preferences travel across tab switches and page reloads.
 *
 * Usage:
 *   const [view, setView] = useViewMode("comms2-contacts-view", "cards");
 *   return (
 *     <div>
 *       <ViewToggle view={view} setView={setView} testid="contacts" />
 *       {view === "cards" ? <CardGrid/> : <RowList/>}
 *     </div>
 *   );
 */
import { useState, useEffect, useCallback } from "react";
import { LayoutGrid, List } from "lucide-react";
import { NAVY, NAVY_HAIRLINE } from "./tokens";

export function useViewMode(key, defaultMode = "cards") {
  const [mode, setModeRaw] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      return v === "cards" || v === "list" ? v : defaultMode;
    } catch { return defaultMode; }
  });
  const setMode = useCallback((v) => {
    setModeRaw(v);
    try { localStorage.setItem(key, v); } catch { /* private mode */ }
  }, [key]);
  useEffect(() => { /* rehydrate on remount if key changed */ }, [key]);
  return [mode, setMode];
}

export default function ViewToggle({ view, setView, testid }) {
  const isCards = view === "cards";
  return (
    <div
      className="inline-flex items-center rounded-md border overflow-hidden"
      style={{ borderColor: NAVY_HAIRLINE }}
      data-testid={testid ? `${testid}-view-toggle` : "view-toggle"}
    >
      <button
        type="button"
        onClick={() => setView("cards")}
        className="h-8 px-2.5 text-[11.5px] font-semibold flex items-center gap-1"
        style={isCards
          ? { background: NAVY, color: "white" }
          : { background: "white", color: NAVY }}
        data-testid={testid ? `${testid}-view-cards` : "view-cards"}
        aria-pressed={isCards}
        title="Card view"
      >
        <LayoutGrid className="w-3.5 h-3.5" /> Cards
      </button>
      <button
        type="button"
        onClick={() => setView("list")}
        className="h-8 px-2.5 text-[11.5px] font-semibold flex items-center gap-1"
        style={!isCards
          ? { background: NAVY, color: "white" }
          : { background: "white", color: NAVY }}
        data-testid={testid ? `${testid}-view-list` : "view-list"}
        aria-pressed={!isCards}
        title="List view"
      >
        <List className="w-3.5 h-3.5" /> List
      </button>
    </div>
  );
}
